import { useEffect, useRef } from 'react';
import { datadogLogs } from '@datadog/browser-logs';

/**
 * A hook to be used to track user clicks and property changes.
 * @param category The category of the event
 * @param action The action which has been performed
 * @param label The control which caused the event
 * @param trackingProp (Optional) - The property whose value change will cause the tracking event to be sent
 * @param clickFn (Optional) - A click handler function which will be called after the tracking call
 * @param keyFn (Optional) - A keyUp handler function which will be called after the tracking call
 * @param additionalPayload - (Optional) additional JSON payload to send to Datadog
 * @returns {{handleItemClick: handleItemClick, handleKeystroke: handleKeystroke}} click and keyup handler functions
 * which will track the click events and call through to the supplied clickFn/keyFn
 */
export const useAnalytics = (
  category,
  action,
  label,
  trackingProp = '',
  clickFn = () => {},
  keyFn = () => {},
  additionalPayload = {}
) => {
  /**
   * Send the analytics to Datadog
   */
  const sendAnalytics = () => {
    // console.log(
    //     `actionName->${category} actionValue-->${action} widgetIdentifier-->${label} additionalPayload-->`,
    //     additionalPayload
    // );
    datadogLogs.logger.info(
      category,
      Object.assign({ actionValue: action, widgetIdentifier: label }, { ...additionalPayload })
    );
  };

  /**
   * this is for tracking clicks on items which are not re-rendered on a user selection
   * i.e. a user clicks on a static link
   */
  const handleItemClick = (event, ...args) => {
    sendAnalytics();
    clickFn(event, ...args);
  };

  /**
   * this is for tracking enter keystrokes on items which are not re-rendered on a user selection
   * i.e. a user presses enter on a static link
   */
  const handleKeystroke = (event, ...args) => {
    if (event.key.toLowerCase().indexOf('enter') === 0) {
      sendAnalytics();
    }
    keyFn(event, ...args);
  };

  const isFirstRun = useRef(true);

  /**
   * This is for tracking the change in a property value - usually caused by a user selecting something
   */
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
    } else {
      sendAnalytics();
    }
  }, [trackingProp]);

  return { handleItemClick, handleKeystroke };
};
